/**
 * Decode a JWT token payload (base64) without verifying the signature.
 * This is safe for client-side expiration checks only.
 */
export function decodeJwtPayload(
  token: string,
): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    // Payload is the second part of the JWT
    const payload = parts[1];
    // Base64 decode (handle both standard and URL-safe base64)
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired by examining its `exp` claim.
 * Returns true if the token is missing, malformed, or past its expiry.
 */
export function isJwtExpired(token: string | null | undefined): boolean {
  if (!token) return true;

  const payload = decodeJwtPayload(token);
  if (!payload) return true;

  const exp = payload.exp as number | undefined;
  if (!exp) return false; // no expiry claim, treat as valid

  const now = Math.floor(Date.now() / 1000);
  return now >= exp;
}
